import { Modal, ModalBody } from '@postscript/components';
import useModalTracking from 'components/campaigns/useModalTracking';
import { FlowType, FlowWizardOption } from 'components/flowBuilder/types';
import { parseFlowType } from 'components/flowBuilder/utils/flows';
import { useEffect, useRef } from 'react';
import {
  COMMON_EVENT_NAMES,
  logEvent,
  MODAL_TYPES,
  PRODUCT_AREAS,
} from 'utils/events';
import { useITCUpsellExperiment } from '../../hooks/useITCUpsellExperiment';
import CreateFlowWizard from '../CreateFlowWizard';

interface ModalProps {
  flowType: FlowType;
  isOpen: boolean;
  onCancel: (from?: FlowWizardOption) => void;
}

export default function CreateFlowModal({
  flowType,
  isOpen,
  onCancel,
}: ModalProps): JSX.Element {
  const { isCampaign } = parseFlowType(flowType);
  const modalType = isCampaign
    ? MODAL_TYPES.CREATE_CAMPAIGN
    : MODAL_TYPES.CREATE_AUTOMATION;

  const {
    variant: itcUpsellVariant,
    eligible: itcUpsellEligible,
    infinityBillingState,
  } = useITCUpsellExperiment();

  const trackClose = useModalTracking({
    isOpen,
    modalType,
    productArea: PRODUCT_AREAS.FLOW_BUILDER,
    extraProperties: isCampaign
      ? {
          experiment_variant: itcUpsellVariant,
          experiment_eligible: itcUpsellEligible,
        }
      : undefined,
  });

  useEffect(() => {
    if (!isOpen) return;
    logEvent(COMMON_EVENT_NAMES.MODAL_OPENED, {
      modal_type: modalType,
      product_area: PRODUCT_AREAS.FLOW_BUILDER,
      ...(isCampaign && {
        experiment_variant: itcUpsellVariant,
        experiment_eligible: itcUpsellEligible,
        infinity_billing_state: infinityBillingState,
      }),
    });
  }, [
    isOpen,
    modalType,
    isCampaign,
    itcUpsellVariant,
    itcUpsellEligible,
    infinityBillingState,
  ]);

  const handleClose = () => {
    trackClose();
    onCancel();
  };

  const headerRef = useRef(null);

  return (
    <Modal
      data-testid={
        isCampaign ? 'create-campaign-modal' : 'create-automation-modal'
      }
      firstFocusableElement={headerRef}
      isOpen={isOpen}
      onCancel={handleClose}
      style={{ width: 'fit-content' }}
    >
      <ModalBody>
        <CreateFlowWizard flowType={flowType} onCancel={handleClose} />
      </ModalBody>
    </Modal>
  );
}
